// Ported from /Users/alexey/projects/Viory/streamogon/frontend/src/stores/presetTargets.ts (streamogon,
// own code): the list sorted by order then name, create / update / delete,
// toggling "active" by a whole update. Adapted: the airgate REST client,
// write-only secrets (a toggle or a move never sends one - omitted keeps),
// reordering by rewriting sort_order, Flussonic observations and the
// "check" of an API binding.

import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'
import { isAbortError } from '@/api/errors'
import type { ID } from '@/api/types'
import { useServices } from '@/app/services'
import { describeError } from '@/shared/errors'
import type {
  FlussonicStreamList,
  FlussonicStreamsRequest,
  OutputObservation,
  TargetPreset,
  TargetPresetImpact,
  TargetPresetRequest,
} from './model'

/** Step between neighbours when the order is rewritten (room to insert by hand). */
export const ORDER_STEP = 10

/** `sort_order` maximum from openapi.yaml (TargetPresetRequest). */
const MAX_ORDER = 1_000_000

export function byOrder(a: TargetPreset, b: TargetPreset): number {
  return a.sort_order - b.sort_order || a.name.localeCompare(b.name) || a.id.localeCompare(b.id)
}

/** The request that replaces p with patch applied, secrets untouched (omitted = kept). */
export function requestFromTarget(p: TargetPreset, patch: Partial<TargetPresetRequest> = {}): TargetPresetRequest {
  const req: TargetPresetRequest = { name: p.name, kind: p.kind, url: p.url, is_active: p.is_active, sort_order: p.sort_order }
  if (p.latency_ms) req.latency_ms = p.latency_ms
  if (p.description) req.description = p.description
  if (p.api_url) req.api_url = p.api_url
  if (p.api_user) req.api_user = p.api_user
  if (p.stream) req.stream = p.stream
  // W10: a toggle or a move must not turn a platform template back into an
  // ordinary preset, nor drop its default profile (an omitted
  // egress_profile_id means "none").
  if (p.platform) req.platform = p.platform
  if (p.egress_profile_id) req.egress_profile_id = p.egress_profile_id
  return { ...req, ...patch }
}

export const useTargetsStore = defineStore('feature-targets', () => {
  const { rest } = useServices()

  const targets = shallowRef<TargetPreset[]>([])
  const observations = shallowRef<ReadonlyMap<ID, OutputObservation>>(new Map())
  const loading = ref(false)
  const loaded = ref(false)
  const error = ref<string | null>(null)

  const sorted = computed(() => [...targets.value].sort(byOrder))

  function upsert(p: TargetPreset): void {
    const i = targets.value.findIndex((x) => x.id === p.id)
    const next = [...targets.value]
    if (i >= 0) next[i] = p
    else next.push(p)
    targets.value = next
  }

  async function load(signal?: AbortSignal): Promise<void> {
    loading.value = true
    try {
      const [list, obs] = await Promise.all([
        rest.call('listTargetPresets', { signal }),
        rest.call('listTargetPresetObservations', { signal }).catch((err: unknown) => {
          // Flussonic monitoring is a bonus: without it the list still works.
          if (isAbortError(err)) throw err
          return [] as OutputObservation[]
        }),
      ])
      targets.value = list
      observations.value = new Map(obs.map((o) => [o.preset_id, o]))
      loaded.value = true
      error.value = null
    } catch (err) {
      if (!isAbortError(err)) error.value = describeError(err)
    } finally {
      loading.value = false
    }
  }

  /** Loads once (the leg form needs the list, not a refresh). */
  async function ensure(signal?: AbortSignal): Promise<void> {
    if (!loaded.value && !loading.value) await load(signal)
  }

  async function refreshObservations(signal?: AbortSignal): Promise<void> {
    try {
      const obs = await rest.call('listTargetPresetObservations', { signal })
      observations.value = new Map(obs.map((o) => [o.preset_id, o]))
    } catch {
      // Keep the last picture; the list itself is unaffected.
    }
  }

  async function create(req: TargetPresetRequest): Promise<TargetPreset> {
    const p = await rest.call('createTargetPreset', { body: req })
    upsert(p)
    return p
  }

  async function update(id: ID, req: TargetPresetRequest): Promise<TargetPreset> {
    const p = await rest.call('updateTargetPreset', { path: { id }, body: req })
    upsert(p)
    return p
  }

  /**
   * What saving req over preset id would touch (W9 TGTB): control runs the
   * edit and rolls it back, so the refusals are those of update.
   */
  function impact(id: ID, req: TargetPresetRequest, signal?: AbortSignal): Promise<TargetPresetImpact> {
    return rest.call('targetPresetImpact', { path: { id }, body: req, signal })
  }

  async function remove(id: ID): Promise<void> {
    await rest.call('deleteTargetPreset', { path: { id } })
    targets.value = targets.value.filter((p) => p.id !== id)
  }

  function setActive(p: TargetPreset, active: boolean): Promise<TargetPreset> {
    return update(p.id, requestFromTarget(p, { is_active: active }))
  }

  /**
   * Moves p one place up (-1) or down (+1) in the list. Touches only p and
   * its neighbour - never the rest of the list, however many presets it
   * holds (incident of 25.09: a tie at sort_order 0 shared by every preset
   * made this rewrite all of them, including targets owned by other
   * channels, on a single click).
   *
   * Distinct orders: the two neighbours simply swap their sort_order (two
   * writes). Equal orders (typically: neither preset has ever been
   * reordered, both still default to 0): a numeric swap would be a no-op,
   * so the neighbour that must now sort later is nudged just past the tie,
   * using the outer neighbour's order for headroom when there is any; the
   * one that must sort earlier keeps its current value (no write for it at
   * all). At most one PUT for a tie, two for distinct orders - either way,
   * no preset other than the pair being swapped is read or written.
   *
   * When the whole neighbourhood shares the same order (the 25.09 case),
   * the nudged preset can end up sorted past the tied run rather than
   * strictly next to its old neighbour - a visible but harmless quirk of
   * having only non-negative integers and no room to spare; it never
   * touches another preset's stored order.
   */
  async function move(p: TargetPreset, dir: -1 | 1): Promise<void> {
    const list = sorted.value
    const i = list.findIndex((x) => x.id === p.id)
    const j = i + dir
    if (i < 0 || j < 0 || j >= list.length) return
    const a = list[i]!
    const b = list[j]!
    if (a.sort_order !== b.sort_order) {
      // Distinct orders: swapping the two is enough (two writes, not N).
      await Promise.all([
        update(a.id, requestFromTarget(a, { sort_order: b.sort_order })),
        update(b.id, requestFromTarget(b, { sort_order: a.sort_order })),
      ])
      return
    }
    const tie = a.sort_order
    const hi = Math.max(i, j)
    const after = hi < list.length - 1 ? list[hi + 1]!.sort_order : undefined
    // Room between the tie and the next distinct order outside the pair, if
    // any; otherwise the smallest possible step (still ORDER_STEP when there
    // is no outer neighbour at all to consider).
    const room = after === undefined ? ORDER_STEP : after - tie - 1
    const bump = Math.max(1, Math.min(ORDER_STEP, room))
    // The other of the pair ends up sorting earlier and keeps its order -
    // no write for it at all.
    const hiItem = dir > 0 ? a : b
    await update(hiItem.id, requestFromTarget(hiItem, { sort_order: Math.min(MAX_ORDER, tie + bump) }))
  }

  /** The streams of a Flussonic API binding (the "check" button). */
  function streams(req: FlussonicStreamsRequest, signal?: AbortSignal): Promise<FlussonicStreamList> {
    return rest.call('listFlussonicStreams', { body: req, signal })
  }

  function byId(id: ID | undefined): TargetPreset | undefined {
    return id ? targets.value.find((p) => p.id === id) : undefined
  }

  return {
    targets,
    sorted,
    observations,
    loading,
    loaded,
    error,
    load,
    ensure,
    refreshObservations,
    create,
    update,
    impact,
    remove,
    setActive,
    move,
    streams,
    byId,
  }
})
